import {useInfiniteQuery, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import axiosAuthorised from "./AxiosAuthorised";
import {
    MessageItemType
} from "../Types/Response/MessageCentre/MessageCentre";
import {PaginatedResponseType} from "../Types/Response/PaginatedResponseType";
import { PaginationPageParameters } from './PaginationHelpers';

export enum MessageItemUpdateType {
    ARCHIVE,
    DELETE,
    MARK_AS_READ,
    REPLY,
    CREATE
}

const startNewMessage = async (data: any) => {
    const newMessageResult = await axiosAuthorised.post(
        `${process.env.REACT_APP_API_HOST}/Mobile/v1/MessageCentre`,
        JSON.stringify(data)
    );

    return newMessageResult;
}

const archiveMessages = async (messageIds: Array<string>, archived: boolean) => {
    await axiosAuthorised.patch(
        `${process.env.REACT_APP_API_HOST}/Mobile/v1/MessageCentre/Thread/Archive`,
        JSON.stringify({ ThreadIds: messageIds, Archive: archived })
    );
}

const deleteMessages = async (messageIds: Array<string>) => {
    await axiosAuthorised.patch(
        `${process.env.REACT_APP_API_HOST}/Mobile/v1/MessageCentre/Thread/Delete`,
        JSON.stringify({ ThreadIds: messageIds })
    );
}

const useInfiniteMessages = (folderName: string, folderId: number) => {
    const baseUrl = `${process.env.REACT_APP_API_HOST}/Mobile/v1/MessageCentre/List?offset=0&limit=20&folderId=${folderId}`;

    return useInfiniteQuery(
        ['messageCentre', folderName],
        async ({ pageParam = baseUrl }) => {
            const { data } : { data: PaginatedResponseType } = await axiosAuthorised.get(pageParam);
            return data;
        },
        {
            ...PaginationPageParameters,
            staleTime: 600000,
        },
    );
}

const useUpdateMessageCentre = (messageIds: Array<number>, folderName: string, skipThreadUpdate?: boolean) => {
    const queryClient = useQueryClient();
    let QUERY_KEY = ["messageCentre", folderName];

    return useMutation(async ({forwardedFunction, newData, updateType}) => forwardedFunction ? forwardedFunction(messageIds, newData) : null, {
        onMutate: async ({forwardedFunction, newData, updateType} : {forwardedFunction: any, newData: any, updateType: MessageItemUpdateType}) => {
            await queryClient.cancelQueries(QUERY_KEY);
            const previousData: any = queryClient.getQueryData(QUERY_KEY);

            if(previousData) {
                const cloneData = JSON.parse(JSON.stringify(previousData));

                cloneData.pages.map((group: any) =>
                    group.data.map((messageItem: MessageItemType) => {
                        if (messageIds.includes(messageItem.id)) {
                            switch (updateType) {
                                case MessageItemUpdateType.MARK_AS_READ:
                                    messageItem.hasNewReply = newData;
                                    break;
                                case MessageItemUpdateType.ARCHIVE:
                                    messageItem.isArchived = newData;
                                    break;
                                case MessageItemUpdateType.REPLY:
                                    messageItem.subject = newData;
                                    messageItem.lastMessageDate = new Date();
                                    break;
                                case MessageItemUpdateType.DELETE:
                                    messageItem.hidden = true;
                                    break;
                            }
                        }

                        return messageItem;
                    })
                )

                queryClient.setQueryData(QUERY_KEY, cloneData);

                messageIds.forEach((message) => {
                    switch (updateType) {
                        case MessageItemUpdateType.ARCHIVE:
                            queryClient.setQueryData(['message', message], (oldData: any) => {
                                return {...oldData, isArchived: newData};
                            });
                            break;
                    }
                })
            }

            return { previousData }
        },
        onError: (err, newData: any, context) => {
            queryClient.setQueryData(QUERY_KEY, context?.previousData)
        },
        onSuccess: () => {
            queryClient.invalidateQueries(["userinfo"]).then(() => null)
        },
        onSettled: () => {
            messageIds.forEach((message) => {
                const previousData: any = queryClient.getQueryData(['message', message]);
                if(!previousData?.id) {
                    queryClient.invalidateQueries(['message', message]).then(() => null);
                }
            })
        }
    })
}

const useMessageCentreDepartments = () => {
    return useQuery(["messageDepartments"], async () => {
        const { data } : { data: PaginatedResponseType } = await axiosAuthorised.get(`${process.env.REACT_APP_API_HOST}/Mobile/v1/MessageCentre/Departments`);
        return data;
    },
    {
        staleTime: 600000,
    })
}

export { useInfiniteMessages, useUpdateMessageCentre, useMessageCentreDepartments, startNewMessage, archiveMessages, deleteMessages }